import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { positionsApi } from '../services/api';
import { Position, CreatePositionData } from '../types';

const PositionsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: positionsData, isLoading } = useQuery({
    queryKey: ['positions', { page, search }],
    queryFn: () => positionsApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreatePositionData>();

  const createMutation = useMutation({
    mutationFn: positionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreatePositionData }) =>
      positionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingPosition(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: positionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const onSubmit = (data: CreatePositionData) => {
    if (editingPosition) {
      updateMutation.mutate({ id: editingPosition.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (position: Position) => {
    setEditingPosition(position);
    reset({
      name: position.name,
      description: position.description || '',
    });
    setShowForm(true);
  };

  const handleDelete = (position: Position) => {
    if (window.confirm(`Are you sure you want to delete "${position.name}"?`)) {
      deleteMutation.mutate(position.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPosition(null);
    reset();
  };

  return (
    <div className="positions-management">
      <div className="page-header">
        <h1>Positions Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Position
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingPosition ? 'Edit Position' : 'Create New Position'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
                placeholder="e.g., Open Position, Closed Position"
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="Describe the position and partner connection"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingPosition ? 'Update' : 'Create'} Position
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search positions..."
          aria-label="Search positions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading positions...</div>
      ) : positionsData && positionsData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Moves Count</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {positionsData.data.map((position) => (
                  <tr key={position.id}>
                    <td>
                      <strong>{position.name}</strong>
                    </td>
                    <td>{position.description || '-'}</td>
                    <td>{position.movesCount || 0}</td>
                    <td>{new Date(position.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(position)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(position)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {positionsData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: positionsData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No positions found. Create your first position!</p>
        </div>
      )}
    </div>
  );
};

export default PositionsManagement;